import type { PropertyFilters } from '@/types/property'
import { PATHS } from '@/routes/paths'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import { listingCanonical, titleCase, truncateMetaDescription, uniqueKeywords } from '../utils'
import { breadcrumbSchema } from '../schemas/common'
import { resolveAreaDisplayLabel } from '@/lib/areaFilterUtils'
import type { ApiFilterOptions } from '@/types/api'

function displayAreaLabel(area: string | undefined, areas?: ApiFilterOptions['areas']) {
  return resolveAreaDisplayLabel(area, areas)
}

function intentLabel(intent?: PropertyFilters['intent']) {
  if (intent === 'rent') return 'for Rent'
  if (intent === 'commercial') return 'Commercial'
  return intent ? 'for Sale' : ''
}

/**
 * Visible listing H1.
 * SEO landings use the resolved payload seo.h1.
 * /properties and other non-SEO routes keep the existing listing title.
 */
export function visibleListingH1(input: {
  isSeoPath: boolean
  payloadH1?: string | null
  fallback: string
}): string {
  if (!input.isSeoPath) return input.fallback
  const h1 = input.payloadH1?.trim()
  return h1 || 'Properties'
}

export function buildListingSeoTitle(
  filters: PropertyFilters,
  areas?: ApiFilterOptions['areas'],
): string {
  const city = titleCase(filters.city) ?? 'India'
  const area = filters.area?.[0] ? displayAreaLabel(filters.area[0], areas) : null
  const searchLabel = titleCase(filters.search)
  const isNewProjects = filters.premium || filters.saleType?.includes('New Property')
  const type = filters.category?.[0] ? titleCase(filters.category[0]) : 'Properties'
  const bhk = filters.bhk?.length === 1 ? titleCase(filters.bhk[0]) : null

  if (bhk && city) {
    const label = intentLabel(filters.intent)
    return `${bhk} ${type} ${label ? `${label} ` : ''}in ${area ? `${area}, ` : ''}${city}`
  }

  if (area) {
    if (!filters.intent && !filters.category?.length) return `Properties in ${area}, ${city}`
    if (filters.intent === 'commercial') return `Commercial Properties in ${area}, ${city}`
    if (isNewProjects) return `New Residential Projects in ${area}, ${city}`
    if (filters.intent === 'rent') return `${type} for Rent in ${area}, ${city}`
    if (!filters.intent) return `${type} in ${area}, ${city}`
    return `${type} for Sale in ${area}, ${city}`
  }

  if (searchLabel) {
    if (filters.intent === 'commercial') return `Commercial Properties near ${searchLabel}, ${city}`
    if (filters.intent === 'rent') return `Properties for Rent near ${searchLabel}, ${city}`
    return `Properties for Sale near ${searchLabel}, ${city}`
  }

  if (isNewProjects) return `New Residential Projects in ${city}`
  if (filters.intent === 'commercial' && filters.category?.[0]) {
    return `Commercial ${type} in ${city}`
  }
  if (filters.intent === 'rent') return `${type} for Rent in ${city}`
  if (filters.category?.[0] && filters.intent !== 'commercial') {
    return filters.intent ? `${type} for Sale in ${city}` : `${type} in ${city}`
  }
  return `Property in ${city}`
}

const FLAT_CATEGORY_RE = /^(flat|flats|apartment|apartments)$/i

/**
 * Browser / search-result title with the live result count, e.g.
 * "22+ Apartments / Flats for Sale in Mavdi, Rajkot". Matches the server SEO landing title format.
 */
export function buildListingDocumentTitle(
  filters: PropertyFilters,
  total: number,
  areas?: ApiFilterOptions['areas'],
): string {
  let title = buildListingSeoTitle(filters, areas)
  const category = filters.category?.[0]
  if (category && FLAT_CATEGORY_RE.test(category.trim())) {
    const typeLabel = titleCase(category) ?? category
    title = title.replace(typeLabel, 'Apartments / Flats')
  }
  if (total >= 2) {
    title = title.replace(/^Property in /, 'Properties in ')
    title = `${total}+ ${title}`
  }
  return title
}

export function buildListingSeoDescription(
  filters: PropertyFilters,
  total: number,
  areas?: ApiFilterOptions['areas'],
): string {
  const title = buildListingSeoTitle(filters, areas)
  const countPart = total > 0 ? `Browse ${total}+ verified listings` : 'Browse verified listings'
  const city = titleCase(filters.city) ?? 'India'
  const raw = `${countPart} for ${title}. Explore photos, prices, amenities and contact sellers on Quick Build Con in ${city}.`
  return truncateMetaDescription(raw)
}

export function buildListingSeoKeywords(
  filters: PropertyFilters,
  areas?: ApiFilterOptions['areas'],
): string {
  const city = (filters.city || 'india').toLowerCase()
  const area = displayAreaLabel(filters.area?.[0], areas)?.toLowerCase()
  const category = filters.category?.[0]?.toLowerCase()
  const bhk = filters.bhk?.[0]?.toLowerCase()
  const intent = filters.intent === 'rent' ? 'rent' : filters.intent === 'commercial' ? 'commercial' : 'sale'

  return uniqueKeywords([
    `property in ${city}`,
    category ? `${category} in ${city}` : null,
    area ? `property in ${area}` : null,
    bhk ? `${bhk} ${city}` : null,
    `${intent} property ${city}`,
    'Quick Build Con',
    'real estate india',
  ])
}

export function buildListingCanonical(filters: PropertyFilters): string {
  const href = buildPropertyHref(
    filters.intent === 'rent'
      ? 'rent'
      : filters.intent === 'buy'
        ? 'buy'
        : filters.intent === 'commercial'
          ? 'commercial'
          : '',
    {
      city: filters.city,
      category: filters.category?.[0],
      area: filters.area?.[0],
      bhk: filters.bhk?.[0],
      search: filters.search,
    },
  )
  const qIndex = href.indexOf('?')
  const path = qIndex >= 0 ? href.slice(0, qIndex) : href
  const qs = qIndex >= 0 ? href.slice(qIndex + 1) : ''
  return listingCanonical(path, qs)
}

export function buildListingBreadcrumbSchema(
  filters: PropertyFilters,
  areas?: ApiFilterOptions['areas'],
) {
  const city = titleCase(filters.city)
  const area = filters.area?.[0] ? displayAreaLabel(filters.area[0], areas) : null
  const crumbs: Array<{ name: string; url?: string }> = [
    { name: 'Home', url: PATHS.home },
  ]

  if (filters.intent === 'rent') {
    crumbs.push({
      name: 'Rent',
      url: filters.city ? buildPropertyHref('rent', { city: filters.city }) : PATHS.properties,
    })
  } else if (filters.intent !== 'commercial') {
    crumbs.push({
      name: 'Buy',
      url: filters.city ? buildPropertyHref('buy', { city: filters.city }) : PATHS.properties,
    })
  }

  if (filters.category?.[0]) {
    crumbs.push({
      name: titleCase(filters.category[0]) ?? filters.category[0],
      url: buildListingCanonical({ ...filters, area: undefined, bhk: undefined, search: undefined }),
    })
  }

  if (city) {
    crumbs.push({
      name: city,
      url: buildListingCanonical({ ...filters, area: undefined, bhk: undefined, search: undefined }),
    })
  }

  if (area) {
    crumbs.push({ name: area })
  } else if (!filters.category?.length && city) {
    crumbs.push({ name: `Properties in ${city}` })
  }

  return breadcrumbSchema(crumbs)
}

export function buildCitySeoTitle(cityName: string): string {
  return `Property in ${cityName} | Quick Build Con`
}

export function buildCitySeoDescription(cityName: string, total?: number): string {
  const count = total && total > 0 ? `${total}+ ` : ''
  return truncateMetaDescription(
    `Find ${count}verified flats, villas, plots and commercial properties in ${cityName}. Buy, rent or sell with QBC Verified listings and local expert support.`,
  )
}
